import { useImperativeHandle, useRef, useState } from "react";
import { BankDetailsNumberField } from "./BankDetailsNumberField";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import classes from "./BankDetails.module.scss";
import type { RefObject } from "react";

const MAX_LENGTH = 2;

type SortCodeParts = {
  sortCode1: string;
  sortCode2: string;
  sortCode3: string;
};

function splitSortCode(sortCode: string): SortCodeParts {
  const sortCode1 = sortCode.slice(0, 2);
  const sortCode2 = sortCode.slice(2, 4);
  const sortCode3 = sortCode.slice(4, 6);

  return { sortCode1, sortCode2, sortCode3 };
}

export interface BankDetailsSortCodeProps {
  initialValue?: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string | null;
  isValid?: boolean | null;
  ref?: RefObject<{ focus: () => void } | null>;
  onBlur?: () => void;
}

export function BankDetailsSortCode(props: BankDetailsSortCodeProps) {
  const {
    initialValue = "",
    onChange,
    label,
    ref,
    isValid,
    error,
    onBlur,
  } = props;

  const {
    sortCode1: initialSortCode1,
    sortCode2: initialSortCode2,
    sortCode3: initialSortCode3,
  } = splitSortCode(initialValue);
  const [sortCode1, setSortCode1] = useState(initialSortCode1);
  const [sortCode2, setSortCode2] = useState(initialSortCode2);
  const [sortCode3, setSortCode3] = useState(initialSortCode3);

  const sortCode1Ref = useRef<HTMLInputElement>(null);
  const sortCode2Ref = useRef<HTMLInputElement>(null);
  const sortCode3Ref = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (sortCode1.length < MAX_LENGTH) {
        sortCode1Ref.current?.focus();
      } else if (sortCode2.length < MAX_LENGTH) {
        sortCode2Ref.current?.focus();
      } else {
        sortCode3Ref.current?.focus();
      }
    },
  }));

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasted.length > 0) {
      e.preventDefault();
      const {
        sortCode1: s1,
        sortCode2: s2,
        sortCode3: s3,
      } = splitSortCode(pasted);
      setSortCode1(s1);
      setSortCode2(s2);
      setSortCode3(s3);
      onChange(`${s1}${s2}${s3}`);

      if (s1.length === MAX_LENGTH && s2.length > 0) {
        sortCode2Ref.current?.focus();
      }
      if (s2.length === MAX_LENGTH && s3.length > 0) {
        sortCode3Ref.current?.focus();
      }
    }
  }

  return (
    <FormControl
      type="fieldset"
      label={label}
      autoFit
      isValid={isValid}
      error={error}
      required
    >
      <Stack gap="md" direction="row">
        <BankDetailsNumberField
          className={classes.sortCodeInput}
          value={sortCode1}
          ref={sortCode1Ref}
          onChange={(value) => {
            setSortCode1(value);
            onChange(`${value}${sortCode2}${sortCode3}`);
            if (value.length === MAX_LENGTH) {
              sortCode2Ref.current?.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && sortCode1.length === 0) {
              e.preventDefault();
            }
          }}
          onPaste={handlePaste}
          maxLength={MAX_LENGTH}
          data-testid="sortCode1"
        />

        <BankDetailsNumberField
          className={classes.sortCodeInput}
          value={sortCode2}
          ref={sortCode2Ref}
          onChange={(value) => {
            setSortCode2(value);
            onChange(`${sortCode1}${value}${sortCode3}`);
            if (value.length === MAX_LENGTH) {
              sortCode3Ref.current?.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && sortCode2.length === 0) {
              sortCode1Ref.current?.focus();
              e.preventDefault();
            }
          }}
          onPaste={handlePaste}
          maxLength={MAX_LENGTH}
          data-testid="sortCode2"
        />

        <BankDetailsNumberField
          className={classes.sortCodeInput}
          value={sortCode3}
          ref={sortCode3Ref}
          onChange={(value) => {
            setSortCode3(value);
            onChange(`${sortCode1}${sortCode2}${value}`);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && sortCode3.length === 0) {
              sortCode2Ref.current?.focus();
              e.preventDefault();
            }
          }}
          onBlur={onBlur}
          onPaste={handlePaste}
          maxLength={MAX_LENGTH}
          data-testid="sortCode3"
        />
      </Stack>
    </FormControl>
  );
}
